import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const Hero = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [currentPartner, setCurrentPartner] = useState(0);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const partners = t("partners.list", { returnObjects: true }).slice(0, 8);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPartner((prev) => (prev + 1) % partners.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [partners.length]);

  return (
    <section
      id="home"
      className="mt-12 pb-12  sm:mt-0 relative min-h-screen flex items-center overflow-hidden"
      style={{ backgroundColor: "var(--background-color)" }}
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0">
        {/* Floating geometric shapes */}
        <div
          className="absolute top-20 left-10 w-32 h-32 rounded-full opacity-10 animate-pulse"
          style={{ backgroundColor: "var(--primary-color)" }}
        ></div>
        <div
          className="absolute top-40 right-20 w-24 h-24 rounded-full opacity-10 animate-pulse delay-1000"
          style={{ backgroundColor: "var(--secondary-color)" }}
        ></div>
        <div
          className="absolute bottom-40 left-1/4 w-20 h-20 rounded-full opacity-10 animate-pulse delay-2000"
          style={{ backgroundColor: "var(--primary-color)" }}
        ></div>
        <div
          className="absolute bottom-20 right-1/3 w-28 h-28 rounded-full opacity-10 animate-pulse delay-3000"
          style={{ backgroundColor: "var(--secondary-color)" }}
        ></div>

        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/30 via-transparent to-teal-50/30"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-cyan-50/20 to-transparent"></div>
      </div>

      <div className="mb-12 relative max-w-7xl mx-auto px-4 md:px-8 w-full">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 lg:gap-12 items-center">
          {/* Left Column - Main Content */}
          <div
            className={`space-y-8 transition-all duration-1000 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 -translate-x-10"
            }`}
          >
            {/* Main Title with Creative Typography */}
            <div className="space-y-6">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="block text-center sm:text-left bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent">
                  {t("home.title")}
                </span>
              </h1>
              <p
                className="text-lg sm:text-xl md:text-2xl leading-relaxed text-center sm:text-left"
                style={{ color: "var(--text-secondary)" }}
              >
                {t("home.subtitle")}
              </p>
            </div>
          </div>

          {/* Right Column - Partners Showcase */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              isVisible
                ? "opacity-100 translate-x-0"
                : "opacity-0 translate-x-10"
            }`}
          >
            <div className="relative">
              {/* Main Partner Display */}
              <div className="bg-white rounded-3xl p-4 sm:p-6 lg:p-8 shadow-2xl border border-gray-100 mb-6 lg:mb-8">
                <h3
                  className="text-lg sm:text-xl lg:text-2xl font-bold mb-4 sm:mb-6 text-center"
                  style={{ color: "var(--text-primary)" }}
                >
                  {t("home.partners")}
                </h3>

                {/* Featured Partner */}
                <div className="text-center mb-6 sm:mb-8">
                  <div className="inline-block p-4 sm:p-6 rounded-2xl bg-gray-50 hover:bg-gray-100 transition-colors duration-300">
                    <img
                      src={partners[currentPartner]?.logo}
                      alt={partners[currentPartner]?.name}
                      className="h-16 sm:h-20 w-auto mx-auto mb-3 sm:mb-4 object-contain"
                      loading="lazy"
                    />
                    <h4
                      className="text-base sm:text-lg font-semibold"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {partners[currentPartner]?.name}
                    </h4>
                  </div>
                </div>

                {/* Partner Indicators */}
                <div className="hidden sm:flex justify-center space-x-1 sm:space-x-2 mb-4 sm:mb-6">
                  {partners.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPartner(index)}
                      className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 ${
                        index === currentPartner
                          ? "bg-emerald-600 scale-125"
                          : "bg-gray-300 hover:bg-emerald-400"
                      }`}
                    />
                  ))}
                </div>

                {/* All Partners Grid */}
                <div className="grid grid-cols-4 gap-2 sm:gap-4">
                  {partners.slice(0, 8).map((partner, index) => (
                    <div
                      key={index}
                      className={`group p-2 sm:p-3 rounded-xl transition-all duration-300 hover:scale-110 cursor-pointer ${
                        index === currentPartner
                          ? "bg-emerald-50 shadow-md"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                      onClick={() => setCurrentPartner(index)}
                    >
                      <img
                        src={partner.logo}
                        alt={partner.name}
                        className="h-6 sm:h-8 w-auto mx-auto object-contain opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating Elements */}
              <div
                className="absolute -top-4 -right-4 w-8 h-8 rounded-full opacity-20 animate-bounce"
                style={{ backgroundColor: "var(--primary-color)" }}
              ></div>
              <div
                className="absolute -bottom-4 -left-4 w-6 h-6 rounded-full opacity-20 animate-bounce delay-1000"
                style={{ backgroundColor: "var(--secondary-color)" }}
              ></div>
            </div>
          </div>
        </div>

        {/* Project Info Cards - Below Partners */}
        <div
          className={`mt-16 transition-all duration-1000 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {/* Duration Card */}
            <div className="group relative bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
              <div className="mb-4">
                <h3
                  className="text-base sm:text-lg font-semibold mb-2"
                  style={{ color: "var(--primary-color)" }}
                >
                  {t("home.projectInfo.duration")}
                </h3>
                <p
                  className="text-xl sm:text-2xl font-bold"
                  style={{ color: "var(--primary-color)" }}
                >
                  {t("home.projectInfo.durationValue")}
                </p>
              </div>
            </div>

            {/* Project Code Card */}
            <div className="group relative bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
              <div className="mb-4">
                <h3
                  className="text-base sm:text-lg font-semibold mb-2"
                  style={{ color: "var(--secondary-color)" }}
                >
                  {t("home.projectInfo.projectCode")}
                </h3>
                <p
                  className="text-xl sm:text-2xl font-bold"
                  style={{ color: "var(--secondary-color)" }}
                >
                  {t("home.projectInfo.projectCodeValue")}
                </p>
              </div>
            </div>

            {/* Erasmus+ Card */}
            <div className="group relative bg-white rounded-2xl p-4 sm:p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center">
              <div className="mb-4">
                <div className="w-32 sm:w-40 lg:w-50 h-16 sm:h-20 mx-auto mb-4 flex items-center justify-center">
                  <img
                    src="/partners/eu-logo.png"
                    alt="Erasmus+ Programme of the European Union"
                    className="w-[100%] h-full object-contain"
                    loading="eager"
                    style={{ filter: "none" }}
                  />
                </div>
                {/* <h3 className="text-lg font-semibold mb-2 text-blue-600">
                  {t('home.projectInfo.fundedBy')}
                </h3>
                <p className="text-lg font-bold text-blue-800 leading-tight">
                  Erasmus+ Programme<br />
                  <span className="text-sm">of the European Union</span>
                </p> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
